// src/components/Colegiado/ColegiadoForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Alert from "@mui/material/Alert";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Grid,
  Select,
  InputLabel,
  FormControl,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import SubirFotoPopup from "../Utiles/SubirFotoPopup";
import AvatarUsuario from "./AvatarUsuario";
import Certificados from "./Certificados";
import ColegiadoRecibos from "./ColegiadoRecibos";

/* -------------------------- Utilidades y validaciones -------------------------- */

const validarDocumentoIdentidad = (doc) => {
  if (!doc) return false;

  const up = String(doc).toUpperCase().trim();
  const letras = "TRWAGMYFPDXBNJZSQVHLCKE";

  // DNI: 8 números + letra
  if (/^[0-9]{8}[A-Z]$/.test(up)) {
    const numero = parseInt(up.substring(0, 8), 10);
    const letraEsperada = letras[numero % 23];
    return up.charAt(8) === letraEsperada;
  }

  // NIE: X/Y/Z + 7 números + letra
  if (/^[XYZ][0-9]{7}[A-Z]$/.test(up)) {
    const prefijos = { X: "0", Y: "1", Z: "2" };
    const numero = parseInt(prefijos[up.charAt(0)] + up.substring(1, 8), 10);
    const letraEsperada = letras[numero % 23];
    return up.charAt(8) === letraEsperada;
  }

  return false;
};

const validarCuentaBancaria = (iban) => {
  if (!iban) return true;
  const ibanLimpio = iban.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(ibanLimpio)) return false;
  const reorden = ibanLimpio.slice(4) + ibanLimpio.slice(0, 4);
  const numerico = reorden.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let total = "";
  for (let i = 0; i < numerico.length; i++) {
    total += numerico[i];
    if (total.length > 9) total = (BigInt(total) % 97n).toString();
  }
  return BigInt(total) % 97n === 1n;
};

const toISODate = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s.slice(0, 10);
};

const asNumberOrEmpty = (v) => {
  if (v === "" || v == null) return "";
  const n = Number(v);
  return Number.isNaN(n) ? "" : n;
};

const uniqueNumberArray = (arr) =>
  Array.from(new Set((Array.isArray(arr) ? arr : []).map((x) => Number(x)).filter((n) => !Number.isNaN(n))));

const asNumberArrayUnique = (arr) => uniqueNumberArray(arr);

// comparación laxa para detectar cambios (arrays sin orden, trims, null -> "")
const normForCompare = (v) => {
  if (Array.isArray(v)) return [...v].map(Number).filter((x) => !Number.isNaN(x)).sort((a, b) => a - b);
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.trim();
  return v;
};
const iguales = (a, b) => {
  const A = normForCompare(a);
  const B = normForCompare(b);
  if (Array.isArray(A) || Array.isArray(B)) return JSON.stringify(A) === JSON.stringify(B);
  return String(A) === String(B);
};

/* -------------------------- Hook de catálogos (simple) -------------------------- */

const useCatalogo = (url) => {
  const [data, setData] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(url);
        if (!alive) return;
        const arr = Array.isArray(res?.data) ? res.data : res?.data?.content ?? [];
        setData(
          (arr || []).map((x) => ({
            id: x.id ?? x.codigo ?? x.value ?? x.key,
            nombre: x.nombre ?? x.descripcion ?? x.titulo ?? String(x.id ?? x.codigo ?? ""),
          }))
        );
      } catch (e) {
        console.error("Error cargando catálogo:", url, e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return data;
};

/* --------------------------- MultiSelect con toggle ---------------------------- */

function MultiSelectChips({ labelId, label, options, value, onChange }) {
  // value: number[]
  const handleToggle = (id) => () => {
    const numId = Number(id);
    const exists = value.includes(numId);
    const next = exists ? value.filter((v) => v !== numId) : [...value, numId];
    onChange(uniqueNumberArray(next));
  };

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      {/* onChange vacío para que NO haga el toggle interno de MUI; gestionamos nosotros */}
      <Select
        labelId={labelId}
        multiple
        value={value.map(String)}
        onChange={() => { }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((id) => {
              const it = options.find((o) => String(o.id) === String(id));
              return <Chip key={id} label={it?.nombre ?? id} />;
            })}
          </Box>
        )}
        MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
      >
        {options.map((op) => {
          const checked = value.includes(Number(op.id));
          return (
            <MenuItem key={op.id} value={op.id} onClick={handleToggle(op.id)}>
              <Checkbox checked={checked} />
              <ListItemText primary={op.nombre} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

/* --------------------------------- Componente --------------------------------- */

const ColegiadoForm = () => {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const { usuario } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [popupAbierto, setPopupAbierto] = useState(false);
  const [tab, setTab] = useState(0); // 0 = Modificar Perfil, 1 = Certificados, 2 = Recibos

  // Descargas / estado
  const [downloading, setDownloading] = useState({}); // clave -> boolean

  // Cursos certificables (p.ej. finalizados o con inscripción pagada)
  const [cursosCertificables, setCursosCertificables] = useState([]);
  const [certMsg, setCertMsg] = useState(null);

  // Mensaje sección contraseña
  const [mensajePwd, setMensajePwd] = useState(null);

  // Catálogos
  const paises = useCatalogo(`${API}/api/paises`);
  const estados = useCatalogo(`${API}/api/estados`);
  const situaciones = useCatalogo(`${API}/api/situaciones-profesionales`);
  const titulaciones = useCatalogo(`${API}/api/titulaciones-principales`);
  const especialidades = useCatalogo(`${API}/api/especialidades`);
  const lugaresTrabajo = useCatalogo(`${API}/api/lugares-trabajo`);

  // Carga del perfil
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/colegiado/perfil`, { headers: authHeader });
        if (!alive) return;
        const c = res?.data || {};
        setPerfil({
          // Personales
          nombre: c.nombre ?? "",
          apellido_1: c.apellido_1 ?? c.apellido1 ?? "",
          apellido_2: c.apellido_2 ?? c.apellido2 ?? "",
          nif: c.nif ?? "",
          fecha_Nacimiento: toISODate(c.fecha_Nacimiento ?? c.fecha_nacimiento ?? ""),
          sexo: (c.sexo ?? "").toString(),

          // Contacto
          direccion: c.direccion ?? "",
          localidad: c.localidad ?? "",
          c_p_: c.c_p_ ?? c.cp ?? "",
          provincia: c.provincia ?? "",
          pais: /^\d+$/.test(String(c.pais ?? "")) ? Number(c.pais) : c.pais ?? "",
          nacionalidad: /^\d+$/.test(String(c.nacionalidad ?? "")) ? Number(c.nacionalidad) : c.nacionalidad ?? "",
          paisNacimientoId: asNumberOrEmpty(c.paisNacimientoId ?? c.pais_nacimiento_id ?? ""),

          telefono_1: c.telefono_1 ?? "",
          telefono_2: c.telefono_2 ?? "",
          email: c.email ?? "",
          email_2: c.email_2 ?? "",

          // Bancarios
          iban: (c.iban ?? "").toUpperCase(),

          // Profesionales
          lugaresTrabajoIds: uniqueNumberArray(
            c.lugaresTrabajoIds ??
            c.lugares_trabajo_ids ??
            (Array.isArray(c.lugares_trabajo) ? c.lugares_trabajo.map((x) => x.id ?? x.codigo) : [])
          ),
          estadoId: asNumberOrEmpty(c.estadoId ?? c.estado_id ?? c?.estado?.id),
          titulacionPrincipalId: asNumberOrEmpty(
            c.titulacionPrincipalId ?? c.titulacion_principal_id ?? c?.titulacion_principal?.id
          ),
          situacionProfesionalId: asNumberOrEmpty(
            c.situacionProfesionalId ?? c.situacion_profesional_id ?? c?.situacion_profesional?.id
          ),
          especialidadesIds: uniqueNumberArray(
            c.especialidadesIds ??
            c.especialidades_ids ??
            (Array.isArray(c.especialidades) ? c.especialidades.map((x) => x.id ?? x.codigo) : [])
          ),
        });
      } catch (e) {
        console.error("Error cargando perfil:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [API, authHeader]);

  // Cargar cursos certificables (p. ej. finalizados o con inscripción pagada)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/colegiado/cursos-certificables`, { headers: authHeader });
        if (!alive) return;
        const arr = Array.isArray(res.data) ? res.data : [];
        const normalizados = arr.map((c) => ({
          id: c.id ?? c.cursoId ?? c.codigo,
          nombre: c.nombre ?? c.titulo ?? `Curso ${c.id ?? ""}`,
        }));
        setCursosCertificables(normalizados);
      } catch (err) {
        console.warn("No se pudieron cargar cursos certificables (usa tu endpoint).", err);
        setCursosCertificables([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [API, authHeader]);

  // Descarga helper
  const descargar = async ({ url, nombreSugerido, claveEstado }) => {
    try {
      setDownloading((d) => ({ ...d, [claveEstado]: true }));
      const res = await axios.get(url, { headers: authHeader, responseType: "blob" });

      // Intentar obtener nombre de archivo del header si viene:
      let filename = nombreSugerido;
      const dispo = res.headers?.["content-disposition"];
      if (dispo && dispo.includes("filename=")) {
        const match = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        if (match?.[1]) filename = decodeURIComponent(match[1]);
      }

      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      setCertMsg({ tipo: "success", texto: "Descarga iniciada." });
    } catch (err) {
      console.error("Error al descargar:", err);
      setCertMsg({ tipo: "error", texto: "No se pudo descargar el documento." });
    } finally {
      setDownloading((d) => ({ ...d, [claveEstado]: false }));
    }
  };

  const etiquetas = {
    nombre: "Nombre",
    apellido_1: "Primer Apellido",
    apellido_2: "Segundo Apellido",
    nif: "NIF / NIE",
    fecha_Nacimiento: "Fecha de Nacimiento",
    sexo: "Sexo",
    direccion: "Dirección",
    localidad: "Localidad",
    c_p_: "Código Postal",
    provincia: "Provincia",
    pais: "País",
    nacionalidad: "Nacionalidad",
    paisNacimientoId: "País de Nacimiento",
    telefono_1: "Teléfono 1",
    telefono_2: "Teléfono 2",
    email: "Correo Electrónico",
    email_2: "Correo Electrónico Secundario",
    iban: "IBAN",
    estadoId: "Estado (solo lectura)",
    titulacionPrincipalId: "Titulación Principal",
    situacionProfesionalId: "Situación Profesional",
    especialidadesIds: "Especialidades",
    lugaresTrabajoIds: "Lugar de Trabajo",
  };

  const camposBloques = [
    {
      titulo: "Datos Personales",
      campos: ["nombre", "apellido_1", "apellido_2", "nif", "fecha_Nacimiento", "sexo", "nacionalidad", "paisNacimientoId"],
    },
    {
      titulo: "Datos de Contacto",
      campos: ["direccion", "localidad", "c_p_", "provincia", "pais", "telefono_1", "telefono_2", "email", "email_2"],
    },
    { titulo: "Datos Bancarios", campos: ["iban"] },
  ];

  const opcionesSexo = [
    { id: "0", nombre: "Masculino (0)" },
    { id: "1", nombre: "Femenino (1)" },
  ];

  const obtenerOpciones = (campo) => {
    switch (campo) {
      case "pais":
      case "nacionalidad":
        return paises;
      case "paisNacimientoId":
        return paises;
      case "estadoId":
        return estados;
      case "situacionProfesionalId":
        return situaciones;
      case "titulacionPrincipalId":
        return titulaciones;
      case "especialidadesIds":
        return especialidades;
      case "lugaresTrabajoIds":
        return lugaresTrabajo;
      case "sexo":
        return opcionesSexo;
      default:
        return [];
    }
  };

  const esCampoSelect = (campo) =>
    [
      "nacionalidad",
      "pais",
      "paisNacimientoId",
      "estadoId",
      "situacionProfesionalId",
      "titulacionPrincipalId",
      "sexo",
      "especialidadesIds",
      "lugaresTrabajoIds",
    ].includes(campo);

  if (!perfil) return <div>Cargando…</div>;

  /* --------------------------- Validación cambio password --------------------------- */
  const PasswordSchema = Yup.object({
    password1: Yup.string()
      .trim()
      .min(8, "La nueva contraseña debe tener al menos 8 caracteres")
      .required("Debes introducir la nueva contraseña"),
    password2: Yup.string()
      .trim()
      .oneOf([Yup.ref("password1")], "Las contraseñas no coinciden")
      .required("Repite la nueva contraseña"),
  });

  const parseErr = (err) => {
    const d = err?.response?.data;
    if (!d) return "No se pudo actualizar la contraseña.";
    if (typeof d === "string") return d;
    try {
      return Object.values(d).join(" • ");
    } catch {
      return JSON.stringify(d);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Modificar Perfil" />
        <Tab label="Certificados" />
        <Tab label="Recibos" />
      </Tabs>

      {/* ======= Pestaña: Modificar Perfil ======= */}
      {tab === 0 && (
        <>
          <Typography variant="h5" gutterBottom>
            Modificar Perfil
          </Typography>

          <Box display="flex" alignItems="center" mb={2} gap={2}>
            <AvatarUsuario usuario={usuario} size={80} />
            <Button variant="outlined" onClick={() => setPopupAbierto(true)}>
              Cambiar foto
            </Button>
          </Box>
          <SubirFotoPopup open={popupAbierto} onClose={() => setPopupAbierto(false)} />

          {/* ---------- FORM Solicitud de cambios de datos ---------- */}
          <Formik
            enableReinitialize
            initialValues={perfil}
            validationSchema={Yup.object({
              nif: Yup.string()
                .required("NIF/NIE obligatorio")
                .test("doc-valido", "NIF o NIE inválido", (v) => validarDocumentoIdentidad(v)),
              email: Yup.string().email("Email no válido").required("Correo obligatorio"),
              iban: Yup.string().test("cuenta-valida", "IBAN no válido", (v) => !v || validarCuentaBancaria(v)),
            })}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              const ignorados = ["estadoId"];

              try {
                const cambios = {};
                const valuesNorm = {
                  ...values,
                  iban: (values.iban || "").replace(/\s+/g, "").toUpperCase(),
                  nombre: values.nombre?.toUpperCase() ?? "",
                  apellido_1: values.apellido_1?.toUpperCase() ?? "",
                  apellido_2: values.apellido_2?.toUpperCase() ?? "",
                  nif: values.nif?.toUpperCase() ?? "",
                  fecha_Nacimiento: toISODate(values.fecha_Nacimiento),

                  lugaresTrabajoIds: uniqueNumberArray(values.lugaresTrabajoIds),
                  especialidadesIds: uniqueNumberArray(values.especialidadesIds),
                  situacionProfesionalId: asNumberOrEmpty(values.situacionProfesionalId),
                  paisNacimientoId: asNumberOrEmpty(values.paisNacimientoId),
                  sexo: (values.sexo ?? "").toString(),
                };

                Object.keys(valuesNorm).forEach((key) => {
                  if (ignorados.includes(key)) return;
                  const nuevo = valuesNorm[key];
                  const anterior = perfil[key];
                  if (!iguales(anterior, nuevo)) {
                    cambios[key] = nuevo;
                  }
                });

                if (Object.keys(cambios).length === 0) {
                  setMensaje({ tipo: "info", texto: "No hay cambios para enviar." });
                  setSubmitting(false);
                  return;
                }

                await axios.post(`${API}/api/cambios/solicitud-cambio`, cambios, {
                  headers: authHeader,
                });

                setMensaje({ tipo: "success", texto: "Solicitud de cambio enviada correctamente." });
                setPerfil(valuesNorm);
                resetForm({ values: valuesNorm });
              } catch (err) {
                console.error("Error al enviar solicitud:", err);
                setMensaje({ tipo: "error", texto: "Error al enviar la solicitud de cambio." });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, handleChange, setFieldValue, errors, touched, isSubmitting }) => {
              // ---- Aquí calculamos una única vez si se ha seleccionado "OTRO" en lugar de trabajo ----
              const tieneOtroLugarTrabajo = (lugaresTrabajo || []).some(
                (lt) =>
                  String(lt.nombre).toUpperCase() === "OTRO" &&
                  (values.lugaresTrabajoIds || [])
                    .map((id) => Number(id))
                    .includes(Number(lt.id))
              );

              return (
                <Form>
                  {/* Bloques editables (personales, contacto, bancarios) */}
                  {camposBloques.map(({ titulo, campos }) => (
                    <Box key={titulo} mb={3}>
                      <Typography variant="h6" gutterBottom>
                        {titulo}
                      </Typography>
                      <Grid container spacing={2}>
                        {campos.map((campo) => {
                          const opciones = obtenerOpciones(campo);
                          const isSelect = esCampoSelect(campo);
                          const isNameSelect = campo === "pais" || campo === "nacionalidad";

                          if (isSelect) {
                            if (campo === "lugaresTrabajoIds" || campo === "especialidadesIds") {
                              // (En estos bloques no se usan, pero dejamos la lógica por consistencia)
                              return (
                                <Grid item xs={12} sm={6} key={campo}>
                                  <MultiSelectChips
                                    labelId={`${campo}-label`}
                                    label={etiquetas[campo]}
                                    options={opciones}
                                    value={values[campo] || []}
                                    onChange={(next) => setFieldValue(campo, next)}
                                  />
                                </Grid>
                              );
                            }

                            // Select normales (incluye país/nacionalidad por nombre, y sexo)
                            return (
                              <Grid item xs={12} sm={6} key={campo}>
                                <FormControl fullWidth>
                                  <InputLabel id={`${campo}-label`} shrink>
                                    {etiquetas[campo]}
                                  </InputLabel>
                                  <Select
                                    labelId={`${campo}-label`}
                                    name={campo}
                                    value={
                                      isNameSelect
                                        ? values[campo] ?? "" // puede ser number o string
                                        : (values[campo] ?? "").toString()
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (campo === "sexo") {
                                        setFieldValue(campo, String(raw));
                                      } else if (isNameSelect) {
                                        const v = /^\d+$/.test(String(raw)) ? Number(raw) : String(raw);
                                        setFieldValue(campo, v);
                                      } else {
                                        setFieldValue(campo, asNumberOrEmpty(raw));
                                      }
                                    }}
                                    renderValue={(selected) => {
                                      if (isNameSelect) {
                                        if (/^\d+$/.test(String(selected))) {
                                          const it = (obtenerOpciones(campo) || []).find(
                                            (o) => String(o.id) === String(selected)
                                          );
                                          return it?.nombre ?? selected ?? "";
                                        }
                                        return selected || "";
                                      }
                                      const it =
                                        (obtenerOpciones(campo) || []).find(
                                          (o) => String(o.id) === String(selected)
                                        ) ||
                                        (obtenerOpciones(campo) || []).find(
                                          (o) => String(o.nombre) === String(selected)
                                        );
                                      return it?.nombre ?? selected ?? "";
                                    }}
                                  >
                                    {(obtenerOpciones(campo) || []).map((op) => (
                                      <MenuItem key={op.id} value={op.id}>
                                        {op.nombre}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            );
                          }

                          // TextField
                          return (
                            <Grid item xs={12} sm={6} key={campo}>
                              <TextField
                                fullWidth
                                name={campo}
                                type={campo === "fecha_Nacimiento" ? "date" : "text"}
                                label={etiquetas[campo]}
                                value={values[campo] || ""}
                                onChange={(e) => {
                                  if (campo === "iban") {
                                    setFieldValue("iban", e.target.value.replace(/\s+/g, "").toUpperCase());
                                  } else if (campo === "fecha_Nacimiento") {
                                    setFieldValue("fecha_Nacimiento", toISODate(e.target.value));
                                  } else {
                                    handleChange(e);
                                  }
                                }}
                                error={Boolean(touched[campo] && errors[campo])}
                                helperText={touched[campo] && errors[campo]}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}

                  {/* Profesionales EDITABLES por solicitud */}
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Datos Profesionales (solicitud editable)
                    </Typography>
                    <Grid container spacing={2}>
                      {/* LUGAR DE TRABAJO → fila completa, Autocomplete ancho */}
                      <Grid item xs={12} sx={{ minWidth: 150 }}>
                        <Autocomplete
                          multiple
                          options={lugaresTrabajo}
                          getOptionLabel={(option) => option?.nombre ?? ""}
                          value={lugaresTrabajo.filter((op) =>
                            (values.lugaresTrabajoIds || [])
                              .map((v) => Number(v))
                              .includes(Number(op.id))
                          )}
                          onChange={(_, newValue) => {
                            const ids = newValue.map((op) => op.id);
                            const normalizados = uniqueNumberArray(ids);
                            setFieldValue("lugaresTrabajoIds", normalizados);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={etiquetas.lugaresTrabajoIds}
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                        {/* Aviso rojo si se ha seleccionado "OTRO" */}
                        {tieneOtroLugarTrabajo && (
                          <Typography variant="body2" sx={{ mt: 1, color: "error.main" }}>
                            Si has seleccionado &quot;OTRO&quot;, por favor notifica el lugar concreto de trabajo
                            mediante un aviso en la app o enviando un email al administrador.
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sx={{ minWidth: 150 }}>
                        <FormControl fullWidth>
                          <InputLabel id="titulacionPrincipalId-label" shrink>
                            {etiquetas.titulacionPrincipalId}
                          </InputLabel>

                          <Select
                            labelId="titulacionPrincipalId-label"
                            name="titulacionPrincipalId"
                            value={(values.titulacionPrincipalId ?? "").toString()}
                            onChange={(e) =>
                              setFieldValue("titulacionPrincipalId", asNumberOrEmpty(e.target.value))
                            }
                          >
                            {titulaciones.map((op) => (
                              <MenuItem key={op.id} value={op.id}>
                                {op.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* SITUACIÓN PROFESIONAL → fila completa, más ancho */}
                      <Grid item xs={12} sx={{ minWidth: 150 }}>
                        <FormControl fullWidth>
                          <InputLabel id="situacionProfesionalId-label" shrink>
                            {etiquetas.situacionProfesionalId}
                          </InputLabel>
                          <Select
                            labelId="situacionProfesionalId-label"
                            name="situacionProfesionalId"
                            value={(values.situacionProfesionalId ?? "").toString()}
                            onChange={(e) =>
                              setFieldValue("situacionProfesionalId", asNumberOrEmpty(e.target.value))
                            }
                          >
                            {situaciones.map((op) => (
                              <MenuItem key={op.id} value={op.id}>
                                {op.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* ESPECIALIDADES → fila completa, MultiSelect ancho */}
                      <Grid item xs={12} sx={{ minWidth: 150 }}>
                        <MultiSelectChips
                          labelId="especialidadesIds-label"
                          label={etiquetas.especialidadesIds}
                          options={especialidades}
                          value={values.especialidadesIds || []}
                          onChange={(next) => setFieldValue("especialidadesIds", next)}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={4}>
                    <Typography variant="h6" gutterBottom>
                      Datos Profesionales (solo lectura)
                    </Typography>
                    <Grid container spacing={2}>
                      {["estadoId"].map((campo) => {
                        const opciones = obtenerOpciones(campo);
                        const valor = Array.isArray(perfil[campo])
                          ? perfil[campo]
                            .map((v) => opciones.find((op) => Number(op.id) === Number(v))?.nombre)
                            .filter(Boolean)
                            .join(", ")
                          : opciones.find((op) => Number(op.id) === Number(perfil[campo]))?.nombre || "";
                        return (
                          <Grid item xs={12} sm={6} key={campo}>
                            <TextField
                              fullWidth
                              label={etiquetas[campo]}
                              value={valor}
                              InputProps={{ readOnly: true }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ backgroundColor: "#b8b8b898" }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    {mensaje && (
                      <Box mt={2}>
                        <Alert severity={mensaje.tipo} onClose={() => setMensaje(null)}>
                          {mensaje.texto}
                        </Alert>
                      </Box>
                    )}
                  </Box>

                  <Grid item xs={12} mt={5}>
                    <Button type="submit" variant="contained" sx={{ px: 3 }} disabled={isSubmitting}>
                      Enviar Solicitud de Cambio
                    </Button>
                  </Grid>
                </Form>
              );
            }}
          </Formik>

          {/* ---------- FORM Cambio de contraseña (independiente) ---------- */}
          <Box mt={6}>
            <Typography variant="h6" gutterBottom>
              Cambiar contraseña
            </Typography>

            <Formik
              initialValues={{ password1: "", password2: "" }}
              validationSchema={PasswordSchema}
              onSubmit={async (vals, { setSubmitting, resetForm }) => {
                setMensajePwd(null);
                const { password1 } = vals;
                try {
                  await axios.put(
                    `${API}/colegiado/password`,
                    { nueva: password1 },
                    { headers: authHeader }
                  );
                  setMensajePwd({ tipo: "success", texto: "Contraseña actualizada correctamente." });
                  resetForm({ values: { password1: "", password2: "" } });
                } catch (err) {
                  console.error("Error actualizando contraseña:", err);
                  const msg =
                    err?.response?.data?.message ||
                    err?.response?.data ||
                    "No se pudo actualizar la contraseña.";
                  setMensajePwd({ tipo: "error", texto: String(msg) });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, handleChange, touched, errors, isSubmitting }) => {
                const [showPwd1, setShowPwd1] = useState(false);
                const [showPwd2, setShowPwd2] = useState(false);

                return (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showPwd1 ? "text" : "password"}
                          name="password1"
                          label="Nueva contraseña"
                          value={values.password1}
                          onChange={handleChange}
                          error={Boolean(touched.password1 && errors.password1)}
                          helperText={touched.password1 && errors.password1}
                          autoComplete="new-password"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPwd1(!showPwd1)} edge="end">
                                  {showPwd1 ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showPwd2 ? "text" : "password"}
                          name="password2"
                          label="Repite la nueva contraseña"
                          value={values.password2}
                          onChange={handleChange}
                          error={Boolean(touched.password2 && errors.password2)}
                          helperText={touched.password2 && errors.password2}
                          autoComplete="new-password"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPwd2(!showPwd2)} edge="end">
                                  {showPwd2 ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    {mensajePwd && (
                      <Box mt={2}>
                        <Alert severity={mensajePwd.tipo} onClose={() => setMensajePwd(null)}>
                          {mensajePwd.texto}
                        </Alert>
                      </Box>
                    )}

                    <Box mt={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !values.password1 || !values.password2}
                      >
                        Actualizar contraseña
                      </Button>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1, color: "text.secondary" }}
                    >
                      * La nueva contraseña debe tener al menos 8 caracteres.
                    </Typography>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </>
      )}

      {/* ======= Pestañas ======= */}
      {tab === 1 && <Certificados />}
      {tab === 2 && <ColegiadoRecibos />}
    </Paper>
  );
};

export default ColegiadoForm;
